import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Input, InputNumber, DatePicker, message, Dropdown, Modal } from 'antd';
import dayjs from 'dayjs';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    SaveOutlined,
} from '@ant-design/icons';

import ListBodyLayout from '@/components/layout/ListBodyLayout';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import Table from '@/components/ui/table/Table';
import AppModal from '../AppModal';
import { EmptyStateBlock, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription, CreateRoleModalBody, CreateRoleModalFooter } from '../style';
import { useCanCheck } from '@/ability/helpers';
import {
    getProjectTimeLogs,
    createProjectTimeLog,
    updateProjectTimeLog,
    deleteProjectTimeLog,
} from '@/services/projects.service';

const TIME_LOGS_QUERY_KEY = (projectId) => ['projects', projectId, 'time-logs'];

export default function ProjectTimeLogsTab({ projectId, project }) {
    const queryClient = useQueryClient();
    const can = useCanCheck();

    const canView = can('projects.time_logs.view');
    const canCreate = can('projects.time_logs.create');
    const canUpdate = can('projects.time_logs.update');
    const canDelete = can('projects.time_logs.delete');

    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingLog, setEditingLog] = useState(null);
    const [form] = Form.useForm();

    const [contextRecord, setContextRecord] = useState(null);
    const [contextVisible, setContextVisible] = useState(false);
    const [contextPosition, setContextPosition] = useState({ x: 0, y: 0 });

    const { data, isLoading, isFetching } = useQuery({
        queryKey: [...TIME_LOGS_QUERY_KEY(projectId), page, limit],
        queryFn: () => getProjectTimeLogs(projectId, { page, limit }),
        enabled: !!projectId && canView,
        keepPreviousData: true,
    });

    const createMutation = useMutation({
        mutationFn: (body) => createProjectTimeLog(projectId, body),
        onSuccess: () => {
            message.success('Time log created');
            queryClient.invalidateQueries(TIME_LOGS_QUERY_KEY(projectId));
            closeModal();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err.message || 'Failed to create time log');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, body }) => updateProjectTimeLog(projectId, id, body),
        onSuccess: () => {
            message.success('Time log updated');
            queryClient.invalidateQueries(TIME_LOGS_QUERY_KEY(projectId));
            closeModal();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err.message || 'Failed to update time log');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteProjectTimeLog(projectId, id),
        onSuccess: () => {
            message.success('Time log deleted');
            queryClient.invalidateQueries(TIME_LOGS_QUERY_KEY(projectId));
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || err.message || 'Failed to delete time log');
        },
    });

    const openModal = (log = null) => {
        setEditingLog(log);
        if (log) {
            form.setFieldsValue({
                workDate: log.workDate ? dayjs(log.workDate) : null,
                hours: log.hours,
                memo: log.memo,
            });
        } else {
            form.resetFields();
        }
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingLog(null);
        form.resetFields();
    };

    const showDeleteConfirm = (log) => {
        Modal.confirm({
            title: `Delete time log?`,
            icon: <ExclamationCircleOutlined style={{ color: '#ef4444' }} />,
            content: 'Are you sure you want to delete this time log? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                deleteMutation.mutate(log.id || log._id);
            },
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const body = {
                ...values,
                workDate: values.workDate ? values.workDate.format('YYYY-MM-DD') : null,
            };

            if (editingLog) {
                updateMutation.mutate({ id: editingLog.id || editingLog._id, body });
            } else {
                createMutation.mutate(body);
            }
        } catch (err) {
            // form validation error
        }
    };

    if (!canView) {
        return (
            <Container $display="flex" $flex="1" $alignItems="center" $justifyContent="center">
                <EmptyStateBlock>
                    <EmptyStateIcon><ClockCircleOutlined /></EmptyStateIcon>
                    <EmptyStateTitle>Access Denied</EmptyStateTitle>
                    <EmptyStateDescription>You do not have permission to view time logs.</EmptyStateDescription>
                </EmptyStateBlock>
            </Container>
        );
    }

    const columns = [
        {
            title: 'Date',
            dataIndex: 'workDate',
            key: 'workDate',
            width: '150px',
            render: (val) => val ? dayjs(val).format('MMM D, YYYY') : '—',
        },
        {
            title: 'Hours',
            dataIndex: 'hours',
            key: 'hours',
            width: '100px',
            render: (val) => val ? Number(val).toFixed(2) : '0.00',
        },
        {
            title: 'Memo',
            dataIndex: 'memo',
            key: 'memo',
            render: (val) => val || '—',
        },
    ];

    const tableData = data?.data || [];
    const total = data?.total || 0;

    return (
        <Container $display="flex" $flexDirection="column" $flex="1" $minHeight="0" $overflow="hidden">
            <ListBodyLayout
                title="Time Logs"
                description="Track and manage time spent on this project."
                actions={
                    canCreate && (
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            icon={<PlusOutlined />}
                            onClick={() => openModal()}
                        >
                            Log Time
                        </Button>
                    )
                }
            >
                <Container $flex="1" $minHeight="0" $overflow="hidden" $display="flex" $flexDirection="column">
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        rowKey={(record) => record.id || record._id}
                        loading={isLoading || isFetching}
                        onRow={(record) => ({
                            onClick: () => {
                                if (canUpdate) openModal(record);
                            },
                            onContextMenu: (e) => {
                                e.preventDefault();
                                setContextRecord(record);
                                setContextPosition({ x: e.clientX, y: e.clientY });
                                setContextVisible(true);
                            },
                            style: { cursor: canUpdate ? 'pointer' : 'default' },
                        })}
                        pagination={{
                            current: page,
                            pageSize: limit,
                            total: total,
                            onChange: (p) => setPage(p),
                        }}
                    />
                </Container>
            </ListBodyLayout>

            {contextVisible && (
                <Dropdown
                    open={contextVisible}
                    onOpenChange={(v) => setContextVisible(v)}
                    menu={{
                        items: [
                            { key: 'edit', label: 'Edit', disabled: !canUpdate },
                            { key: 'delete', label: 'Delete', danger: true, disabled: !canDelete },
                        ],
                        onClick: ({ key }) => {
                            setContextVisible(false);
                            if (key === 'edit') {
                                openModal(contextRecord);
                            } else if (key === 'delete') {
                                showDeleteConfirm(contextRecord);
                            }
                        },
                    }}
                >
                    <div
                        style={{
                            position: 'fixed',
                            left: contextPosition.x,
                            top: contextPosition.y,
                            width: 1,
                            height: 1,
                            zIndex: 1000,
                            pointerEvents: 'none',
                        }}
                    />
                </Dropdown>
            )}

            <AppModal
                size="md"
                open={modalOpen}
                onCancel={closeModal}
                title={editingLog ? 'Edit Time Log' : 'Log Time'}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button $primary={false} $size="14px" onClick={closeModal}>Cancel</Button>
                        <Button
                            $primary $blackOnLight $size="14px"
                            loading={createMutation.isPending || updateMutation.isPending}
                            icon={<SaveOutlined />}
                            onClick={handleSubmit}
                        >
                            {editingLog ? 'Save Changes' : 'Log Time'}
                        </Button>
                    </CreateRoleModalFooter>
                }
                destroyOnClose
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ padding: '24px' }}>
                        <Form.Item
                            name="workDate"
                            label="Date"
                            rules={[{ required: true, message: 'Please select a date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="hours"
                            label="Hours"
                            rules={[{ required: true, message: 'Please enter hours' }]}
                        >
                            <InputNumber min={0.1} step={0.5} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="memo"
                            label="Memo"
                            rules={[{ required: true, message: 'Please enter a memo' }]}
                        >
                            <Input.TextArea rows={3} placeholder="What did you work on?" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
            </AppModal>
        </Container>
    );
}
